// src/components/shared/useStapelverarbeitung.jsx
// Verarbeitet mehrere Dateien nacheinander über dieselbe Extraktions-Pipeline wie die
// Einzelextraktion (POST /extract/default, siehe useDokumentExtraktion) - läuft aber komplett
// eigenständig, unabhängig vom "aktuell in der UI ausgewählten" file/images-State. Jedes Ergebnis
// wird sofort per onErgebnis gemeldet; der Hook selbst kennt die Ergebnisliste nicht, das
// Einsortieren in rechnungListe übernimmt der Aufrufer (RechnungTab).
//
// Fehlerverhalten: Schlägt die Extraktion einer einzelnen Datei fehl (Fehler, Timeout, kein
// Ergebnis), bricht der Lauf NICHT ab - statt dessen wird ein Ergebnis mit ungültiger Validation
// (Fehlertext in remarks) gemeldet, und der Lauf macht mit der nächsten Datei weiter.
//
// Ausnahme davon: Ist das Dokumenten-Kontingent aufgebraucht (403, nur Rolle "Tester"), bricht
// der Lauf SOFORT ab - die gerade abgelehnte Datei erzeugt bewusst KEINE Fehlerzeile (sie wurde
// ja gar nicht verarbeitet), stattdessen wird onKontingentAufgebraucht mit der Anzahl bereits
// verarbeiteter Dateien gemeldet, damit der Aufrufer eine passende Abschlussmeldung zeigen kann.
//
// Abbruch: abbrechen() setzt ein Flag, das die Schleife vor der nächsten Datei stoppt, UND
// bricht den gerade laufenden Request über AbortController ab. Die gerade abgebrochene Datei
// erzeugt dabei bewusst KEINE Fehlerzeile in der Tabelle (Abbruch ist kein Extraktionsfehler).
import { useRef, useState } from 'react'
import api from '../../api'
import { useAuth } from '../../authContext'
import { readFileAsBase64 } from './useDokumentExtraktion'

export function useStapelverarbeitung (
  promptKey,
  { onErgebnis, onKontingentAufgebraucht }
) {
  const { token, aktualisiereKontingent } = useAuth()
  const [stapelLaeuft, setStapelLaeuft] = useState(false)
  // { aktuell, gesamt } während des Laufs, sonst null
  const [fortschritt, setFortschritt] = useState(null)
  const abbruchAngefordert = useRef(false)
  const laufenderController = useRef(null)

  const fehlermeldungAus = err =>
    err.response?.data?.detail || err.response?.data?.title || err.message

  const starteStapel = async dateien => {
    if (!dateien || dateien.length === 0) return

    abbruchAngefordert.current = false
    setStapelLaeuft(true)
    setFortschritt({ aktuell: 0, gesamt: dateien.length })

    let prompt
    try {
      const promptResponse = await api.get(
        `/prompts/${encodeURIComponent(promptKey)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      prompt = promptResponse.data.text
    } catch (err) {
      // Prompt konnte gar nicht geladen werden - jede Datei bekommt dieselbe Fehlerzeile,
      // damit der Benutzer trotzdem einen vollständigen Überblick über den (gescheiterten)
      // Stapellauf in der Tabelle sieht.
      const meldung = `Prompt konnte nicht geladen werden: ${fehlermeldungAus(
        err
      )}`
      dateien.forEach(file =>
        onErgebnis({
          file,
          dokument: {},
          validation: { valid: false, remarks: meldung }
        })
      )
      setStapelLaeuft(false)
      setFortschritt(null)
      return
    }

    for (let i = 0; i < dateien.length; i++) {
      if (abbruchAngefordert.current) break

      const file = dateien[i]
      const controller = new AbortController()
      laufenderController.current = controller

      try {
        const documentBase64 = await readFileAsBase64(file)
        const extractResponse = await api.post(
          '/extract/default',
          {
            documentBase64,
            fileExtension: '.' + file.name.split('.').pop(),
            prompt
          },
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: controller.signal
          }
        )

        const { finalJson, finalValidation } = extractResponse.data
        if (!finalJson) {
          onErgebnis({
            file,
            dokument: {},
            validation: finalValidation || {
              valid: false,
              remarks: 'Die Extraktion hat kein Ergebnis geliefert.'
            }
          })
        } else {
          onErgebnis({
            file,
            dokument: JSON.parse(finalJson),
            validation: finalValidation || null
          })
        }
      } catch (err) {
        if (abbruchAngefordert.current) {
          break // Abbruch während dieser Datei: keine Fehlerzeile, Lauf endet hier
        }
        if (err.response?.status === 403) {
          // Kontingent aufgebraucht: i Dateien wurden bereits verarbeitet (Zeilen 0..i-1),
          // diese hier (i) nicht mehr - keine Fehlerzeile, Lauf endet sofort.
          onKontingentAufgebraucht?.({ verarbeitet: i, gesamt: dateien.length })
          break
        }
        onErgebnis({
          file,
          dokument: {},
          validation: {
            valid: false,
            remarks: `Fehler bei der Extraktion: ${fehlermeldungAus(err)}`
          }
        })
      } finally {
        laufenderController.current = null
        setFortschritt({ aktuell: i + 1, gesamt: dateien.length })
      }
    }

    setStapelLaeuft(false)
    setFortschritt(null)
    aktualisiereKontingent()
  }

  const abbrechen = () => {
    abbruchAngefordert.current = true
    laufenderController.current?.abort()
  }

  return { stapelLaeuft, fortschritt, starteStapel, abbrechen }
}

export default useStapelverarbeitung
